import '../index.css'

import styles from './ContentView.module.css'

import { LuArrowLeft } from '@react-icons/all-files/lu/LuArrowLeft'
import cn from 'clsx'
import React, { useCallback, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { setTrackMediaEventProperties } from '@disco/state-management/actions/MediaActions'
import {
  addToDisco,
  addToDiscoCancel,
} from '@disco/state-management/actions/PublicPlaylistActions'
import { getDiscoverPlayAudioMixpanelProperties } from '@disco/utils/mixpanel/discover-page'

import { getAuthTransferUrl } from 'utils/routing'

import AddToDiscoModal from 'views/pages/public-playlist/playlist/add-to-disco-modal'

import DocumentHeadTags from 'components/document-head-tags'
import { Heading } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import DiscoverHeader from '../discover-header'
import {
  useCatalogActions,
  useContentData,
  useContentPlayback,
  useIsPlaylistCreatorOpen,
  useRedirectIfNoData,
} from '../hooks'
import {
  useDiscoverySearchFilterCategories,
  useDiscoverySearchFilters,
} from '../hooks/discovery-search-filters'
import type { ContentType } from '../utils'
import { getDisplayValues } from '../utils'
import ContentHero from './content-hero'
import ContentTracks from './content-tracks'
import { useGetContentTracks } from './content-tracks/hooks'

// Special client library ID to trigger the /_b/h= proxy for cross-domain requests.
// This allows Save to DISCO to work from the discover page (that has no client library) by routing through
// the Lambda@Edge proxy that handles add_to_disco API calls.
const DISCOVER_CLIENT_LIBRARY_ID = -1

interface ContentViewProps {
  location: Location
  contentType?: ContentType
}

const ContentView = ({
  location,
  contentType = 'playlist',
}: ContentViewProps): React.ReactNode => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // Get discoAdd state for the modal
  const discoAdd = useSelector(state => state.discoAdd)
  const user = useSelector(state => state.user)

  // Get content data from navigation state
  const { playlist, catalog, hasData } = useContentData(location, contentType)

  // Playlist creator state
  const isPlaylistCreatorOpen = useIsPlaylistCreatorOpen()

  // Redirect if no data
  useRedirectIfNoData(hasData)

  // Search and filter state for DiscoverHeader
  const [query, setQuery] = useState<string>('')
  const categoryNames = useDiscoverySearchFilterCategories()
  const { activeFilter, handleFilterChange, clearFilters } =
    useDiscoverySearchFilters({ query })

  // Fetch tracks based on content type
  const {
    tracks,
    isLoading: isLoadingTracks,
    error: tracksError,
  } = useGetContentTracks(contentType, playlist, catalog)

  // Get content ID
  const contentId =
    contentType === 'playlist' ? playlist?.id || 0 : catalog?.id || 0
  // Only playlists have business with business_id
  const contentBusinessId = playlist?.business?.business_id || undefined
  // Get display values
  const displayValues = useMemo(
    () => getDisplayValues(contentType, playlist, catalog),
    [contentType, playlist, catalog],
  )
  // Mixpanel properties for track-level events
  const filterTrackMediaEventProperties = useMemo(() => {
    if (contentType === 'playlist') {
      return {
        playlistId: contentId,
        playlist: displayValues.title,
        playlistBusiness: displayValues.businessName || 'Unknown',
        playlistBusinessId: contentBusinessId || undefined,
        context: 'Curated Playlist' as const,
      }
    } else if (contentType === 'catalog') {
      return {
        catalogId: contentId,
        catalogName: displayValues.title,
        catalogBusiness: displayValues.businessName || 'Unknown',
        context: 'Preview Tracks' as const,
      }
    }
    return null
  }, [contentType, contentId, displayValues, contentBusinessId])

  // Mixpanel properties for Play All event - set at track level when Play All is clicked
  const audioMixpanelPayload = useMemo(() => {
    const trackId = tracks[0]?.id // Will be set at first track of track list for handlePlayAll
    if (!filterTrackMediaEventProperties || !trackId) return
    return getDiscoverPlayAudioMixpanelProperties({
      trackId: trackId, // Will be set at first track handlePlayAll
      ...filterTrackMediaEventProperties,
    })
  }, [filterTrackMediaEventProperties, tracks])

  // Memoized function to get Mixpanel properties based on content type for track-level events
  const handleSetAudioMixpanelPayloadPlayAll = useCallback(() => {
    const trackId = tracks[0]?.id // Will be set at first track of track list for handlePlayAll
    if (!trackId || !audioMixpanelPayload) return
    dispatch(
      setTrackMediaEventProperties({
        trackId: trackId,
        properties: { ...audioMixpanelPayload, 'Play Type': 'Play All' },
      }),
    )
  }, [dispatch, tracks, audioMixpanelPayload])

  // Playback controls
  const { isCurrentlyPlaying, handlePlayAll } = useContentPlayback(
    tracks,
    contentId,
    handleSetAudioMixpanelPayloadPlayAll,
  )

  // Catalog-specific actions
  const {
    isFavorite,
    hasAccess,
    catalogStatus,
    handleFavoriteClick,
    handleRequestAccess,
  } = useCatalogActions(catalog, displayValues.businessName)

  // Contact click handler
  const handleContactClick = useCallback(() => {
    const email = catalog?.contact_details
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      window.location.href = `mailto:${email}`
    }
  }, [catalog?.contact_details])

  // Build catalog URL once for "Visit Catalog" external link
  const catalogUrl = useMemo(
    () => (catalog?.id ? getAuthTransferUrl(catalog.id) : undefined),
    [catalog?.id],
  )

  // Header handlers
  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value)
    },
    [],
  )

  const handleClearAll = useCallback(() => {
    setQuery('')
    clearFilters()
  }, [clearFilters])

  const handleSearch = useCallback(() => {
    if (query) {
      navigate(`/discover/search?q=${query}`)
    } else {
      navigate('/discover')
    }
  }, [query, navigate])

  // Save to DISCO handler - capture URL at click time to ensure it's fresh
  const handleSaveToDisco = useCallback(() => {
    if (!playlist) return

    const sourceUrl = playlist.view_playlist_url || window.location.href

    const object = {
      type: 'PLAYLIST' as const,
      playlist_id: playlist.id,
    }

    dispatch(
      addToDisco(
        null, // business - will be selected in modal
        sourceUrl,
        object,
        undefined, // loginDetails
        {
          playlistId: playlist.id,
          clientLibraryId: DISCOVER_CLIENT_LIBRARY_ID,
        },
        {
          Context: 'Curated Playlist',
          'Playlist ID': playlist.id,
          Playlist: playlist.name || 'Untitled Playlist',
          'Playlist Business': playlist.business?.business_name || 'Unknown',
          'Playlist Business ID': playlist.business?.business_id,
          Location: 'Discover Music',
          Type: 'Playlist',
        },
      ),
    )
  }, [dispatch, playlist])

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    dispatch(addToDiscoCancel())
  }, [dispatch])

  if (!hasData) {
    return null
  }

  const addToDiscoModal = discoAdd?.addToDiscoModal
  const showAddToDiscoModal =
    contentType === 'playlist' && addToDiscoModal?.shown && playlist

  return (
    <div
      className={cn(
        'Page',
        'Page--discover',
        isPlaylistCreatorOpen && 'Page--playlistCreatorOpen',
      )}
    >
      <DocumentHeadTags title={`${displayValues.title} | DISCO`} />
      {showAddToDiscoModal && addToDiscoModal && (
        <AddToDiscoModal
          showModal={addToDiscoModal.shown}
          // currentBusinessId=0 means no business context (discover page is not owned by a specific business)
          // This ensures no business is excluded from the selection list
          currentBusinessId={0}
          shareId={null}
          playlistId={playlist.id}
          // TODO: Curated playlists don't have versioning. Using playlist.id for playlistVersionId
          // works because the backend currently accepts matching IDs. If backend validation changes
          // to require distinct IDs, this will break. Consider updating the curated playlist API
          // to return a version_id or making playlistVersionId optional for curated playlists.
          playlistVersionId={playlist.id}
          object={addToDiscoModal.object}
          success={addToDiscoModal.success}
          businesses={addToDiscoModal.businesses || []}
          mixpanelEventDetails={addToDiscoModal.mixpanelEventDetails}
          user={user}
          onRequestClose={handleCloseModal}
          href={playlist.view_playlist_url || window.location.href}
          clientLibraryId={DISCOVER_CLIENT_LIBRARY_ID}
        />
      )}
      <DiscoverHeader
        query={query}
        filter={activeFilter}
        categories={categoryNames}
        onQueryChange={handleQueryChange}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
        onSearch={handleSearch}
      />
      <div className="Page_Content LibrariesList">
        <button
          className={styles.backLink}
          aria-label="Go back"
          onClick={() =>
            location.key !== 'default' ? navigate(-1) : navigate('/discover')
          }
        >
          <LuArrowLeft size={16} />
          <span>Back</span>
        </button>

        <ContentHero
          contentType={contentType}
          contentId={contentId}
          displayTitle={displayValues.title}
          displayImage={displayValues.image}
          displayDescription={displayValues.description}
          displayCuratedBy={displayValues.curatedBy}
          isLoadingTracks={isLoadingTracks}
          isCurrentlyPlaying={isCurrentlyPlaying}
          onPlayAll={handlePlayAll}
          // TODO: Temporarily hidden - Save to DISCO button
          //  onSaveToDisco={
          //    contentType === 'playlist' ? handleSaveToDisco : undefined
          //  }
          tracksCount={tracks.length}
          isFavorite={isFavorite}
          hasAccess={hasAccess}
          catalogStatus={catalogStatus ?? undefined}
          catalogUrl={catalogUrl}
          catalogContactDetails={catalog?.contact_details}
          onFavoriteClick={handleFavoriteClick}
          onRequestAccess={handleRequestAccess}
          onContactClick={handleContactClick}
        />

        {contentType === 'catalog' && (
          <div className={styles.sectionHeader}>
            <Heading as="h3" size="4">
              Featured tracks
            </Heading>
          </div>
        )}
        <ContentTracks
          tracks={tracks}
          contentType={contentType}
          contentId={contentId}
          contentName={displayValues.title}
          contentBusinessId={contentBusinessId}
          businessName={displayValues.businessName || undefined}
          isLoading={isLoadingTracks}
          error={tracksError}
        />
      </div>
    </div>
  )
}

export default ContentView
